import * as Phaser from 'phaser';
import { pointerScreenMoved } from '../../sim/aimStick.ts';
import { rgb24, type Rgba } from '../../sim/color.ts';
import { PIXELS_PER_METER, metersToPixels, toCompassBearing, toDisplayKmh } from '../../sim/constants.ts';
import { SEAFLOOR_COLOR } from '../../sim/environment/water.ts';
import { formatScaleLabel, gridCellMeters, gridLineStarts } from '../../sim/grid.ts';
import { DEFAULT_AIM_CHROME } from '../../sim/map/aimChrome.ts';
import {
  isValidDestination,
  projectMovementArea,
  resolveMove,
  solveTurnArc,
} from '../../sim/movement.ts';
import { clipMovePathAheadOfBow, projectMovePath, type MovePath } from '../../sim/path.ts';
import { nextRingMode, replaySpeed, type RingMode } from '../../sim/replay.ts';
import type { BoatState, Vec2 } from '../../sim/types.ts';
import {
  importBoatDocument,
  UNTITLED_BOAT_NAME,
  canRedoBoat,
  canUndoBoat,
  cloneBoatDocument,
  cloneSideViewColors,
  createBoat,
  createSkiffBoat,
  createBoatHistory,
  normalizeBoat,
  pushBoatUndo,
  reachAxesFromMeters,
  reachWorldPoints,
  redoBoat,
  serializeBoat,
  setBoatName,
  suggestBoatFilename,
  undoBoat,
  headreachFeel,
  headreachLevel,
  planingFeel,
  planingTurnSpeed,
  type BoatColorKey,
  type BoatDocument,
  type BoatHistory,
  type BoatNumberKey,
  type FittingKey,
  type ReachAxes,
} from '../../sim/boat/index.ts';
import { confirmDialog, promptBoatFilename } from '../../ui/boats/dialogs.ts';
import {
  canWriteRepoBoat,
  clearBoatDraft,
  deleteRepoBoat,
  downloadBoatFile,
  loadBoatDraft,
  pickBoatJsonFile,
  saveBoatDraft,
  writeRepoBoat,
} from '../../ui/boats/persist.ts';
import { mountBoatWorkshop, type BoatWorkshop } from '../../ui/boats/workshop.tsx';
import type { SceneScale } from '../../ui/sceneScale.tsx';
import { mountSceneScale } from '../../ui/sceneScale.tsx';
import { isFloatingPanelOpen } from '../../ui/floatingPanel.tsx';
import { mountOverlay, showOverlay } from '../../ui/overlay.tsx';
import { isTypingInField } from '../../ui/overlayTyping.ts';
import { clearEscapeOwner, setEscapeOwner } from '../../ui/pause/escape.ts';
import { isPauseMenuOpen, mountPauseMenu, type PauseMenu } from '../../ui/pause/menu.tsx';
import { overlayStore } from '../../ui/panels.ts';
import { findBoatCatalogEntry, loadBoatCatalog, type BoatCatalogEntry } from '../boats/catalog.ts';
import { loadBoatSvgSource, loadPaintedBoatTexture } from '../boats/liveryTexture.ts';
import { findMapCatalogEntry, loadMapCatalog } from '../maps/catalog.ts';
import { handoffFromWorkshop, mapTestChoices, type TestChoice } from '../testPlay.ts';
import { validateMap } from '../../sim/map/index.ts';
import { CameraControl } from '../cameraControl.ts';
import { setEditorCursor } from '../cursors.ts';
import { clearSceneSearch } from '../mode.ts';
import {
  AREA_STROKE_PX,
  WIND_GHOST_FILL_ALPHA,
  WIND_GHOST_STROKE_ALPHA,
} from '../planningCircle.ts';
import {
  DEFAULT_PLAY_CAMERA,
  appliedZoom,
  computePlayCamera,
  emptyCameraAim,
  nextUserZoomFactor,
  smoothToward,
  stepCameraAim,
  zoomSmoothness,
  type CameraAimState,
} from '../playCamera.ts';
import { setPlayCursor } from '../playCursor.ts';
import {
  cameraWorldRect,
  GRID_SCREEN_PX,
  GRID_STROKE_ALPHA,
  GRID_STROKE_COLOR,
  MAX_ZOOM,
  PLAY_MIN_ZOOM,
} from '../view.ts';

const WORLD_M = 800;
/** A step darker than noon race water. Races keep the shared day colour. */
const POND_WATER_COLOR = 0x6eb6d4;
const POND_WATER_ALPHA = 0.55;
const BOW_UP = -Math.PI / 2;
const MOVE_LINE = 0xe24a4a;
const MOVE_DURATION_MS = 700;
const PAN_DRAG_THRESHOLD_PX = 8;

export type BoatSceneData = {
  boat?: BoatDocument;
  fileName?: string;
  /** Last saved serialize. Set when returning from a test so unsaved edits stay dirty. */
  baselineJson?: string;
};

function overlayCapturesKeys(): boolean {
  const panels = overlayStore.get();
  return (
    isTypingInField() ||
    isFloatingPanelOpen() ||
    isPauseMenuOpen() ||
    panels.editorDialog !== null
  );
}

function strokeChain(
  gfx: Phaser.GameObjects.Graphics,
  points: readonly Vec2[],
): void {
  const first = points[0];

  if (!first || points.length < 2) {
    return;
  }

  gfx.beginPath();
  gfx.moveTo(first.x, first.y);

  for (let index = 1; index < points.length; index += 1) {
    const point = points[index];

    if (point) {
      gfx.lineTo(point.x, point.y);
    }
  }

  gfx.strokePath();
}

function fillReach(
  gfx: Phaser.GameObjects.Graphics,
  points: readonly Vec2[],
  color: number,
  alpha: number,
): void {
  if (points.length < 3) {
    return;
  }

  gfx.fillStyle(color, alpha);
  gfx.fillPoints(
    points.map((point) => new Phaser.Math.Vector2(point.x, point.y)),
    true,
    true,
  );
}

function toCurve(move: MovePath): Phaser.Curves.Path {
  const start = move.segments[0]?.p0 ?? { x: 0, y: 0 };
  const path = new Phaser.Curves.Path(start.x, start.y);

  for (const segment of move.segments) {
    path.add(
      new Phaser.Curves.CubicBezier(
        new Phaser.Math.Vector2(segment.p0.x, segment.p0.y),
        new Phaser.Math.Vector2(segment.p1.x, segment.p1.y),
        new Phaser.Math.Vector2(segment.p2.x, segment.p2.y),
        new Phaser.Math.Vector2(segment.p3.x, segment.p3.y),
      ),
    );
  }

  return path;
}

export class BoatScene extends Phaser.Scene {
  private boat: BoatDocument = createBoat();
  private fileName: string | null = null;
  private catalog: BoatCatalogEntry[] = [];
  private savedJson = '';
  private history: BoatHistory = createBoatHistory();
  private colorBaseline: BoatDocument | null = null;
  private pose: BoatState = { position: { x: 0, y: 0 }, heading: BOW_UP, speed: 0 };
  private hover: Vec2 | null = null;
  private hoverPointerScreen: Vec2 | null = null;
  private resolving = false;
  private moveTween: Phaser.Tweens.Tween | null = null;
  private moveToken = 0;
  private moveFromSpeed = 0;
  private moveProgress = 1;
  private tracking = true;
  private userZoomFactor = 1;
  private cameraAim: CameraAimState = emptyCameraAim();
  private leftOrigin: Vec2 | null = null;
  private rightOrigin: Vec2 | null = null;
  private panPointerId: number | null = null;
  private compareIds = new Set<string>();
  private showGrid = true;
  /** Session ring. Off until the Ring menu or P selects Planing or Headreach. */
  private ringMode: RingMode = 'off';
  private workshop: BoatWorkshop | null = null;
  private sceneScale: SceneScale | null = null;
  private pauseMenu: PauseMenu | null = null;
  private cameraControl = new CameraControl({
    minZoom: PLAY_MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    constrainFit: 'overlap',
    usePhaserBoundsWhenMapFillsView: true,
  });
  private water: Phaser.GameObjects.Graphics | null = null;
  private grid: Phaser.GameObjects.Graphics | null = null;
  private chrome: Phaser.GameObjects.Graphics | null = null;
  private hull: Phaser.GameObjects.Image | null = null;
  private origin = 0;
  private worldPx = 0;
  private drawnGridKey = '';
  private svgSource: string | null = null;
  private liveryToken = 0;
  private liveryKey: string | null = null;
  private booting = false;
  private testMaps: TestChoice[] = [];

  constructor() {
    super('BoatScene');
  }

  create(data: BoatSceneData = {}): void {
    setPlayCursor(false);
    setEditorCursor('select');
    const overlay = mountOverlay();
    showOverlay(overlay);

    this.workshop?.destroy();
    this.pauseMenu = null;
    this.colorBaseline = null;
    this.hover = null;
    this.hoverPointerScreen = null;
    this.history = createBoatHistory();
    this.leftOrigin = null;
    this.rightOrigin = null;
    this.panPointerId = null;
    this.compareIds.clear();
    this.ringMode = 'off';
    this.userZoomFactor = 1;
    this.cameraAim = emptyCameraAim();
    this.stopMove();
    this.cameraControl.reset();

    this.worldPx = metersToPixels(WORLD_M);
    this.origin = this.worldPx / 2;
    this.cameraControl.setWorldBounds({ width: this.worldPx, height: this.worldPx });

    const cam = this.cameras.main;
    cam.setBackgroundColor(SEAFLOOR_COLOR);
    cam.centerOn(this.origin, this.origin);
    this.input.mouse?.disableContextMenu();

    this.water = this.add.graphics();
    this.grid = this.add.graphics().setDepth(0.4);
    this.chrome = this.add.graphics().setDepth(2);
    this.hull = this.add.image(this.origin, this.origin, 'boat').setOrigin(0.5, 0.5).setDepth(1);
    this.resetPose();
    this.lockFollow();
    this.snapPlayCamera();
    this.paintWater();

    this.sceneScale?.destroy();
    this.sceneScale = mountSceneScale('hud');

    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.input.on('pointerupoutside', this.onPointerUp, this);
    this.input.on('wheel', this.onWheel, this);
    window.addEventListener('keydown', this.onKeyDown, true);
    window.addEventListener('pointerdown', this.onNativePointerDown, true);
    window.addEventListener('pointermove', this.onNativePointerMove, true);
    window.addEventListener('pointerup', this.onNativePointerUp, true);
    window.addEventListener('pointercancel', this.onNativePointerUp, true);

    const escapeToken = setEscapeOwner({
      onEscape: () => this.handleEscape(),
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      clearEscapeOwner(escapeToken);
      this.destroyPause();
      this.workshop?.destroy();
      this.workshop = null;
      this.sceneScale?.destroy();
      this.sceneScale = null;
      window.removeEventListener('keydown', this.onKeyDown, true);
      window.removeEventListener('pointerdown', this.onNativePointerDown, true);
      window.removeEventListener('pointermove', this.onNativePointerMove, true);
      window.removeEventListener('pointerup', this.onNativePointerUp, true);
      window.removeEventListener('pointercancel', this.onNativePointerUp, true);
      this.stopMove();
      this.input.off('pointerdown', this.onPointerDown, this);
      this.input.off('pointerup', this.onPointerUp, this);
      this.input.off('pointerupoutside', this.onPointerUp, this);
      this.input.off('wheel', this.onWheel, this);
      setEditorCursor(null);
      this.game.canvas.parentElement?.removeAttribute('data-boat-workshop');
      this.water = null;
      this.grid = null;
      this.chrome = null;
      this.hull = null;
    });

    void this.boot(data);
  }

  update(): void {
    if (!this.hull) {
      return;
    }

    this.updatePlayCamera();
    this.refreshHover();
    this.redrawGrid();
    this.drawChrome();
    this.workshop?.sync();
    this.syncScale();
  }

  private mountChrome(): void {
    this.workshop?.destroy();
    this.workshop = mountBoatWorkshop({
      getState: () => this.toolbarState(),
      actions: {
        onNumber: (key, value) => this.commitNumber(key, value),
        onColor: (key, value) => this.previewColor(key, value),
        onColorCommit: () => this.commitColor(),
        onColorCancel: () => this.cancelColor(),
        onToggleFitting: (key) => this.commitFitting(key),
        onRename: (raw) => this.commitRename(raw),
        onUndo: () => this.handleUndo(),
        onRedo: () => this.handleRedo(),
        onNew: () => void this.handleNew(),
        onImport: () => void this.handleImport(),
        onOpenBoat: (id) => void this.openCatalogBoat(id),
        onToggleCompare: (id) => this.toggleCompare(id),
        onToggleGrid: () => this.toggleGrid(),
        onSetRingMode: (mode) => this.setRingMode(mode),
        onResetSkiff: () => this.resetToSkiff(),
        onResetPose: () => this.resetTrial(),
        onSave: () => void this.handleSave(),
        onDelete: () => void this.handleDelete(),
        onClose: () => this.handleClose(),
        onTestMap: (id) => void this.handleTestMap(id),
        refreshTestMaps: () => {
          void this.refreshTestMaps();
        },
      },
    });
  }

  private async boot(data: BoatSceneData): Promise<void> {
    this.booting = true;

    try {
      if (data.boat) {
        this.prepare(data.boat, data.fileName ?? null);

        if (data.baselineJson !== undefined) {
          this.savedJson = data.baselineJson;

          if (serializeBoat(this.boat) === this.savedJson) {
            clearBoatDraft();
          } else {
            saveBoatDraft(this.boat, this.fileName);
          }
        }
      } else {
        const catalog = await loadBoatCatalog();

        if (!this.sys.isActive()) {
          return;
        }

        const entry = findBoatCatalogEntry('skiff', catalog) ?? catalog[0];
        this.prepare(entry ? entry.boat : createBoat(), entry ? `${entry.id}.json` : null);
      }

      this.mountChrome();
      await this.refreshCatalog();
      await this.refreshTestMaps();
      this.reveal();

      if (data.baselineJson === undefined) {
        await this.offerDraft();
      }
    } finally {
      this.booting = false;
    }
  }

  /** Sets the working boat without touching chrome. `reveal` paints it. */
  private prepare(boat: BoatDocument, fileName: string | null): void {
    this.boat = cloneBoatDocument(boat);
    normalizeBoat(this.boat);
    this.fileName = fileName;
    this.history = createBoatHistory();
    this.colorBaseline = null;
    this.savedJson = serializeBoat(this.boat);
    const id = fileName?.replace(/\.json$/i, '') ?? null;

    if (id) {
      this.compareIds.delete(id);
    }

    this.resetPose();
  }

  private reveal(): void {
    this.placeHull();
    this.drawChrome();
    this.syncDom();
    this.workshop?.sync();
    void this.paintHull();
  }

  private adopt(boat: BoatDocument, fileName: string | null, clean: boolean): void {
    this.prepare(boat, fileName);

    if (clean) {
      clearBoatDraft();
    } else {
      this.noteDraft();
    }

    this.reveal();
  }

  private async offerDraft(): Promise<void> {
    const draft = loadBoatDraft();

    if (!draft || !this.sys.isActive()) {
      return;
    }

    if (draft.fileName !== this.fileName) {
      return;
    }

    if (serializeBoat(draft.boat) === this.savedJson) {
      clearBoatDraft();
      return;
    }

    const resume = await confirmDialog({
      title: 'Resume editing?',
      message: 'You have unsaved changes from a previous session.',
      confirmLabel: 'Resume',
      cancelLabel: 'Start fresh',
    });

    if (!this.sys.isActive()) {
      return;
    }

    if (!resume) {
      clearBoatDraft();
      return;
    }

    const savedJson = this.savedJson;
    this.boat = cloneBoatDocument(draft.boat);
    normalizeBoat(this.boat);
    this.fileName = draft.fileName;
    this.savedJson = savedJson;
    this.history = createBoatHistory();
    this.placeHull();
    this.drawChrome();
    this.syncDom();
    this.workshop?.sync();
    void this.paintHull();
  }

  private toolbarState() {
    const currentBoatId = this.fileName?.replace(/\.json$/i, '') ?? null;

    return {
      boat: cloneBoatDocument(this.boat),
      fileName: this.fileName ?? UNTITLED_BOAT_NAME,
      boats: this.catalog.map((entry) => ({ id: entry.id, label: entry.label })),
      compareIds: [...this.compareIds],
      showGrid: this.showGrid,
      ringMode: this.ringMode,
      currentBoatId,
      canUndo: canUndoBoat(this.history),
      canRedo: canRedoBoat(this.history),
      canResetPose: !this.atRest(),
      canDelete: this.fileName !== null,
      readout: this.trialReadout(),
      testMaps: this.testMaps,
    };
  }

  private async refreshCatalog(): Promise<void> {
    const entries = await loadBoatCatalog();

    if (!this.sys.isActive()) {
      return;
    }

    this.catalog = [...entries];
    const current = this.fileName?.replace(/\.json$/i, '') ?? null;

    for (const id of this.compareIds) {
      if (id === current || !this.catalog.some((entry) => entry.id === id)) {
        this.compareIds.delete(id);
      }
    }

    this.workshop?.sync();
  }

  private async confirmDiscardIfDirty(): Promise<boolean> {
    if (serializeBoat(this.boat) === this.savedJson) {
      return true;
    }

    return confirmDialog({
      title: 'Discard changes?',
      message: 'You have unsaved changes.',
      confirmLabel: 'Discard',
      cancelLabel: 'Cancel',
    });
  }

  private commitNumber(key: BoatNumberKey, value: number): void {
    const before = cloneBoatDocument(this.boat);
    this.boat[key] = value;
    normalizeBoat(this.boat);

    if (serializeBoat(before) === serializeBoat(this.boat)) {
      return;
    }

    pushBoatUndo(this.history, before);
    this.afterEdit();
  }

  private resetToSkiff(): void {
    const before = cloneBoatDocument(this.boat);
    const skiff = createSkiffBoat();
    this.boat.lengthM = skiff.lengthM;
    this.boat.beamM = skiff.beamM;
    this.boat.draftM = skiff.draftM;
    this.boat.planingSpeedKmh = skiff.planingSpeedKmh;
    this.boat.turnM = skiff.turnM;
    this.boat.accelM = skiff.accelM;
    this.boat.brakeM = skiff.brakeM;
    this.boat.hull = { ...skiff.hull };
    this.boat.deck = { ...skiff.deck };
    this.boat.accent = { ...skiff.accent };
    this.boat.side = cloneSideViewColors(skiff.side);
    this.boat.fittings = { ...skiff.fittings };
    normalizeBoat(this.boat);
    this.colorBaseline = null;

    if (serializeBoat(before) === serializeBoat(this.boat)) {
      return;
    }

    pushBoatUndo(this.history, before);
    this.afterEdit();
    void this.paintHull();
  }

  private commitFitting(key: FittingKey): void {
    const before = cloneBoatDocument(this.boat);
    this.boat.fittings[key] = !this.boat.fittings[key];

    if (serializeBoat(before) === serializeBoat(this.boat)) {
      return;
    }

    pushBoatUndo(this.history, before);
    this.afterEdit();
  }

  private commitRename(raw: string): void {
    const before = cloneBoatDocument(this.boat);
    setBoatName(this.boat, raw);

    if (serializeBoat(before) === serializeBoat(this.boat)) {
      return;
    }

    pushBoatUndo(this.history, before);
    this.afterEdit();
  }

  private previewColor(key: BoatColorKey, value: Rgba): void {
    if (!this.colorBaseline) {
      this.colorBaseline = cloneBoatDocument(this.boat);
    }

    this.boat.side[key] = value;
    normalizeBoat(this.boat);
    this.noteDraft();
    this.workshop?.sync();
  }

  private commitColor(): void {
    if (this.colorBaseline && serializeBoat(this.colorBaseline) !== serializeBoat(this.boat)) {
      pushBoatUndo(this.history, this.colorBaseline);
    }

    this.colorBaseline = null;
    this.noteDraft();
    this.workshop?.sync();
  }

  private cancelColor(): void {
    if (!this.colorBaseline) {
      return;
    }

    this.boat = this.colorBaseline;
    this.colorBaseline = null;
    this.noteDraft();
    this.placeHull();
    this.workshop?.sync();
    void this.paintHull();
  }

  private afterEdit(): void {
    this.noteDraft();
    this.placeHull();
    this.drawChrome();
    this.syncDom();
    this.workshop?.sync();
  }

  private noteDraft(): void {
    if (serializeBoat(this.boat) === this.savedJson) {
      clearBoatDraft();
      return;
    }

    saveBoatDraft(this.boat, this.fileName);
  }

  private handleUndo(): void {
    const previous = undoBoat(this.history, this.boat);

    if (!previous) {
      return;
    }

    this.boat = previous;
    this.afterEdit();
  }

  private handleRedo(): void {
    const next = redoBoat(this.history, this.boat);

    if (!next) {
      return;
    }

    this.boat = next;
    this.afterEdit();
  }

  private async handleNew(): Promise<void> {
    if (serializeBoat(this.boat) !== this.savedJson) {
      const discard = await confirmDialog({
        title: 'New boat',
        message: 'Discard unsaved changes?',
        confirmLabel: 'Discard',
        cancelLabel: 'Cancel',
      });

      if (!discard || !this.sys.isActive()) {
        return;
      }
    }

    this.adopt(createBoat(), null, true);
  }

  private async handleSave(): Promise<void> {
    let filename = this.fileName;

    if (!filename) {
      const suggested = suggestBoatFilename(this.boat.name) ?? 'boat.json';
      filename = await promptBoatFilename(suggested);

      if (!this.sys.isActive() || !filename) {
        return;
      }
    }

    if (!canWriteRepoBoat()) {
      downloadBoatFile(this.boat, filename);
      this.fileName = filename;
      this.savedJson = serializeBoat(this.boat);
      clearBoatDraft();
      this.syncDom();
      this.workshop?.sync();
      this.workshop?.showMessage('Downloaded. Catalog writes need the dev server.', 'warning');
      return;
    }

    const wrote = await writeRepoBoat(filename, serializeBoat(this.boat));

    if (!this.sys.isActive()) {
      return;
    }

    if (!wrote) {
      this.workshop?.showMessage('Could not save that boat.', 'error');
      return;
    }

    this.fileName = filename;
    this.savedJson = serializeBoat(this.boat);
    clearBoatDraft();
    this.syncDom();
    this.workshop?.sync();
    this.workshop?.showMessage('Boat saved.', 'success');
    await this.refreshCatalog();
  }

  private async handleDelete(): Promise<void> {
    if (!this.fileName) {
      return;
    }

    const confirmed = await confirmDialog({
      title: 'Delete boat',
      message: `Delete ${this.fileName}?`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });

    if (!confirmed || !this.sys.isActive()) {
      return;
    }

    const catalog = await loadBoatCatalog();

    if (catalog.length <= 1) {
      this.workshop?.showMessage('Keep at least one boat.', 'error');
      return;
    }

    if (!canWriteRepoBoat()) {
      this.workshop?.showMessage('Deleting a catalog boat needs the dev server.', 'warning');
      return;
    }

    const removed = await deleteRepoBoat(this.fileName);

    if (!this.sys.isActive()) {
      return;
    }

    if (!removed) {
      this.workshop?.showMessage('Could not delete that boat.', 'error');
      return;
    }

    const stem = this.fileName.replace(/\.json$/i, '');
    const next = (await loadBoatCatalog()).find((entry) => entry.id !== stem);
    this.adopt(next ? next.boat : createBoat(), next ? `${next.id}.json` : null, true);
    await this.refreshCatalog();
    this.workshop?.showMessage('Boat deleted.', 'success');
  }

  private async openCatalogBoat(id: string): Promise<void> {
    if (!(await this.confirmDiscardIfDirty()) || !this.sys.isActive()) {
      return;
    }

    const catalog = await loadBoatCatalog();
    const entry = findBoatCatalogEntry(id, catalog);

    if (!this.sys.isActive()) {
      return;
    }

    if (!entry) {
      this.workshop?.showMessage('That boat is no longer in the catalog.', 'error');
      await this.refreshCatalog();
      return;
    }

    this.adopt(entry.boat, `${entry.id}.json`, true);
    await this.refreshCatalog();
  }

  private async handleImport(): Promise<void> {
    const picked = await pickBoatJsonFile();

    if (!this.sys.isActive() || picked.status === 'cancelled') {
      return;
    }

    if (picked.status === 'unreadable') {
      this.workshop?.showMessage('Could not read that file.', 'error');
      return;
    }

    const boat = importBoatDocument(picked.json);

    if (!boat) {
      this.workshop?.showMessage('That file is not a boat.', 'error');
      return;
    }

    if (!(await this.confirmDiscardIfDirty()) || !this.sys.isActive()) {
      return;
    }

    this.adopt(boat, null, true);
    this.savedJson = '';
    this.noteDraft();
    this.workshop?.showMessage('Boat imported. Save it to keep a copy in the catalog.', 'success');
  }

  private handleEscape(): boolean {
    if (this.pauseMenu) {
      this.closePause();
      return true;
    }

    this.openPause();
    return true;
  }

  private openPause(): void {
    if (this.pauseMenu) {
      return;
    }

    this.scene.pause();
    this.pauseMenu = mountPauseMenu({
      scene: 'boats',
      onResume: () => this.closePause(),
      onEditor: () => this.leaveTo('editor'),
      onBoats: () => undefined,
      onMainMenu: () => this.leaveTo('start'),
    });
  }

  private closePause(): void {
    this.destroyPause();

    if (this.scene.isPaused()) {
      this.scene.resume();
    }
  }

  private destroyPause(): void {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
  }

  private leaveTo(next: 'start' | 'editor'): void {
    clearSceneSearch();

    if (this.scene.isPaused()) {
      this.scene.resume();
    }

    if (next === 'editor') {
      this.scene.start('EditorScene', {});
      return;
    }

    this.scene.start('StartScene');
  }

  private handleClose(): void {
    this.leaveTo('start');
  }

  private toggleGrid(): void {
    this.showGrid = !this.showGrid;
    this.drawnGridKey = '';
    this.redrawGrid();
    this.workshop?.sync();
  }

  /**
   * 10 m cells fixed on the water. The race grid keeps a constant screen spacing, so
   * its lines drift when the camera zooms. Here the hull stays planted on the same
   * crossings while look-ahead and speed zoom move the view.
   */
  private redrawGrid(): void {
    const gfx = this.grid;
    const cam = this.cameras.main;

    if (!gfx) {
      return;
    }

    const key = this.showGrid
      ? `${cam.scrollX.toFixed(2)}:${cam.scrollY.toFixed(2)}:${cam.zoom.toFixed(4)}`
      : 'off';

    if (key === this.drawnGridKey) {
      return;
    }

    this.drawnGridKey = key;
    gfx.clear();

    if (!this.showGrid || cam.zoom <= 0) {
      return;
    }

    const view = cameraWorldRect(cam.scrollX, cam.scrollY, cam.width, cam.height, cam.zoom);
    const cell = GRID_SCREEN_PX;
    const minX = Math.max(0, view.x - cell);
    const maxX = Math.min(this.worldPx, view.x + view.width + cell);
    const minY = Math.max(0, view.y - cell);
    const maxY = Math.min(this.worldPx, view.y + view.height + cell);

    if (maxX <= minX || maxY <= minY) {
      return;
    }

    gfx.lineStyle(1 / cam.zoom, GRID_STROKE_COLOR, GRID_STROKE_ALPHA);

    for (const worldX of gridLineStarts(minX, maxX, cell)) {
      gfx.lineBetween(worldX, minY, worldX, maxY);
    }

    for (const worldY of gridLineStarts(minY, maxY, cell)) {
      gfx.lineBetween(minX, worldY, maxX, worldY);
    }
  }

  private paintWater(): void {
    const water = this.water;

    if (!water) {
      return;
    }

    water.clear();
    water.fillStyle(SEAFLOOR_COLOR, 1);
    water.fillRect(0, 0, this.worldPx, this.worldPx);
    water.fillStyle(POND_WATER_COLOR, POND_WATER_ALPHA);
    water.fillRect(0, 0, this.worldPx, this.worldPx);
  }

  private restPose(): BoatState {
    return {
      position: { x: this.origin, y: this.origin },
      heading: BOW_UP,
      speed: 0,
    };
  }

  private resetPose(): void {
    this.stopMove();
    this.pose = this.restPose();
    this.hover = null;
    this.hoverPointerScreen = null;
    this.cameraAim = emptyCameraAim();
    this.placeHull();
  }

  /** Parks the trial at the pond centre. Speed on the card drops to zero with it. */
  private resetTrial(): void {
    if (this.atRest()) {
      return;
    }

    this.cameraControl.endPan();
    this.resetPose();
    this.lockFollow();
    this.snapPlayCamera();
    this.workshop?.sync();
  }

  private trialReadout() {
    const boat = this.cameraBoat();

    return {
      speedKmh: toDisplayKmh(boat.speed),
      bearing: toCompassBearing(boat.heading),
      windDirectionDeg: 0,
      windSpeed: 0,
      ...this.ringMeter(),
    };
  }

  private syncScale(): void {
    const zoom = this.cameras?.main.zoom ?? 1;
    this.sceneScale?.setLabel(
      formatScaleLabel(gridCellMeters(zoom, PIXELS_PER_METER, GRID_SCREEN_PX)),
    );
  }

  /** Hover landing while aiming; otherwise the boat's current speed. */
  private ringMeter(): { ringMode: RingMode; meterLevel: number } {
    if (this.ringMode === 'off') {
      return { ringMode: 'off', meterLevel: 0 };
    }

    const speed =
      !this.resolving && this.hover
        ? this.landedMove(this.pose, this.hover).speed
        : this.cameraBoat().speed;

    if (this.ringMode === 'headreach') {
      return { ringMode: 'headreach', meterLevel: headreachLevel(speed) };
    }

    return { ringMode: 'planing', meterLevel: this.projectionFor(speed).level };
  }

  private setRingMode(next: RingMode): void {
    if (next === this.ringMode) {
      return;
    }

    this.ringMode = next;
    this.hoverPointerScreen = null;
    this.drawChrome();
    this.workshop?.sync();
  }

  private atRest(): boolean {
    if (this.resolving) {
      return false;
    }

    const pose = this.pose;

    return (
      pose.speed === 0 &&
      pose.heading === BOW_UP &&
      pose.position.x === this.origin &&
      pose.position.y === this.origin
    );
  }

  private placeHull(): void {
    const hull = this.hull;

    if (!hull) {
      return;
    }

    hull.setDisplaySize(metersToPixels(this.boat.lengthM), metersToPixels(this.boat.beamM));

    if (this.resolving) {
      return;
    }

    hull.setPosition(this.pose.position.x, this.pose.position.y);
    hull.setRotation(this.pose.heading);
  }

  private authoredAxes(boat: BoatDocument = this.boat): ReachAxes {
    return reachAxesFromMeters(boat.turnM, boat.accelM, boat.brakeM);
  }

  private projectionFor(speedPx: number, boat: BoatDocument = this.boat) {
    const authored = this.authoredAxes(boat);

    if (this.ringMode === 'planing') {
      return planingFeel(speedPx, boat.planingSpeedKmh, authored);
    }

    if (this.ringMode === 'headreach') {
      return headreachFeel(speedPx, authored);
    }

    return {
      level: 0,
      coastPx: Math.max(0, speedPx),
      axes: authored,
    };
  }

  private coastCenter(pose: BoatState, coastPx: number): Vec2 {
    return projectMovementArea(pose, 0, { x: 0, y: 0 }, coastPx).center;
  }

  /** Turn bleed only while Planing, and only for an on-plane side click. */
  private landedMove(pose: BoatState, destination: Vec2): BoatState {
    const projected = this.projectionFor(pose.speed);

    if (this.ringMode !== 'planing') {
      return resolveMove(pose, destination, 0);
    }

    const arc = solveTurnArc(pose, destination, 0);
    const kept = arc
      ? planingTurnSpeed({
          chord: arc.speed,
          offset: arc.offset,
          speedPx: pose.speed,
          planingSpeedKmh: this.boat.planingSpeedKmh,
          turnPx: projected.axes.turn,
          coastPx: projected.coastPx,
        })
      : null;

    return resolveMove(pose, destination, 0, kept ?? undefined);
  }

  private reachRadiusPx(): number {
    return metersToPixels(Math.max(this.boat.turnM, this.boat.accelM, this.boat.brakeM));
  }

  private cameraBoat(): BoatState {
    const hull = this.hull;
    const speed = this.resolving
      ? replaySpeed(this.moveFromSpeed, this.pose.speed, this.moveProgress)
      : this.pose.speed;

    return {
      position: hull ? { x: hull.x, y: hull.y } : { ...this.pose.position },
      heading: hull ? hull.rotation : this.pose.heading,
      speed,
    };
  }

  private playCameraTarget() {
    const cam = this.cameras.main;
    const boat = this.cameraBoat();

    return computePlayCamera({
      hull: boat.position,
      area: projectMovementArea(
        boat,
        this.reachRadiusPx(),
        { x: 0, y: 0 },
        this.projectionFor(boat.speed).coastPx,
      ),
      speed: boat.speed,
      viewportWidth: cam.width,
      viewportHeight: cam.height,
      settings: DEFAULT_PLAY_CAMERA,
      userZoomFactor: this.userZoomFactor,
      aim: this.cameraAim.point,
      aimWeight: this.cameraAim.weight,
    });
  }

  private lockFollow(): void {
    const hull = this.hull;
    const cam = this.cameras.main;

    if (!hull) {
      return;
    }

    const scrollX = cam.scrollX;
    const scrollY = cam.scrollY;
    this.tracking = true;
    cam.startFollow(hull, true, 1, 1, cam.followOffset.x, cam.followOffset.y);
    cam.setScroll(scrollX, scrollY);
  }

  private snapPlayCamera(): void {
    const cam = this.cameras.main;
    const target = this.playCameraTarget();
    cam.setZoom(target.appliedZoom);
    cam.setFollowOffset(target.followOffset.x, target.followOffset.y);
    this.cameraControl.constrain(cam);
  }

  private updatePlayCamera(): void {
    if (!this.tracking || this.cameraControl.panning) {
      return;
    }

    const cam = this.cameras.main;
    const dt = this.game.loop.delta / 1000;
    const settings = DEFAULT_PLAY_CAMERA;
    this.cameraAim = stepCameraAim(
      this.cameraAim,
      this.resolving ? null : this.hover,
      this.resolving ? 'fade' : 'hold',
      settings.smoothness,
      dt,
    );
    const target = this.playCameraTarget();
    cam.setFollowOffset(
      smoothToward(cam.followOffset.x, target.followOffset.x, settings.smoothness, dt),
      smoothToward(cam.followOffset.y, target.followOffset.y, settings.smoothness, dt),
    );
    const nextZoom = smoothToward(
      cam.zoom,
      target.appliedZoom,
      zoomSmoothness(settings, cam.zoom, target.appliedZoom),
      dt,
    );

    if (Math.abs(nextZoom - cam.zoom) > 1e-4) {
      cam.setZoom(nextZoom);
    }

    this.cameraControl.constrain(cam);
  }

  private resumeFollow(): void {
    const hull = this.hull;
    const cam = this.cameras.main;

    if (!hull) {
      return;
    }

    const focusX = cam.scrollX + cam.width / (2 * cam.zoom);
    const focusY = cam.scrollY + cam.height / (2 * cam.zoom);
    const scrollX = cam.scrollX;
    const scrollY = cam.scrollY;
    this.tracking = true;
    cam.startFollow(hull, true, 1, 1, hull.x - focusX, hull.y - focusY);
    cam.setScroll(scrollX, scrollY);
  }

  private stopMove(): void {
    this.moveToken += 1;
    this.moveTween?.stop();
    this.moveTween = null;
    this.resolving = false;
    this.moveProgress = 1;
  }

  private commitDestination(destination: Vec2): void {
    if (this.resolving) {
      return;
    }

    const pose = this.pose;
    const projected = this.projectionFor(pose.speed);

    if (
      !isValidDestination(
        pose,
        destination,
        projectMovementArea(pose, this.reachRadiusPx(), { x: 0, y: 0 }, projected.coastPx),
        projected.axes,
      )
    ) {
      return;
    }

    const landed = this.landedMove(pose, destination);
    const path = projectMovePath(pose, destination, 'arc', 0);
    this.hover = null;
    this.hoverPointerScreen = null;
    this.moveFromSpeed = pose.speed;
    this.pose = landed;

    if (!path) {
      this.placeHull();
      this.drawChrome();
      return;
    }

    this.resolving = true;
    this.moveProgress = 0;
    this.travel(path);
    this.drawChrome();
  }

  private travel(path: MovePath): void {
    const curve = toCurve(path);
    const point = new Phaser.Math.Vector2();
    const tangent = new Phaser.Math.Vector2();
    const token = ++this.moveToken;

    this.moveTween = this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: MOVE_DURATION_MS,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        if (token !== this.moveToken || !this.hull) {
          return;
        }

        const progress = tween.getValue() ?? 1;
        this.moveProgress = progress;
        const at = curve.getPoint(progress, point);
        const direction = curve.getTangent(progress, tangent);

        if (at) {
          this.hull.setPosition(at.x, at.y);
        }

        if (direction) {
          this.hull.setRotation(Math.atan2(direction.y, direction.x));
        }
      },
      onComplete: () => {
        if (token !== this.moveToken) {
          return;
        }

        this.moveTween = null;
        this.resolving = false;
        this.moveProgress = 1;
        this.placeHull();
        this.drawChrome();
      },
    });
  }

  private toggleCompare(id: string): void {
    if (this.compareIds.has(id)) {
      this.compareIds.delete(id);
    } else {
      this.compareIds.add(id);
    }

    this.workshop?.sync();
    this.drawChrome();
  }

  private refreshHover(): void {
    if (this.resolving || this.cameraControl.panning || !this.tracking) {
      return;
    }

    const pointer = this.input.activePointer;
    const screen = { x: pointer.x, y: pointer.y };

    if (!pointerScreenMoved(this.hoverPointerScreen, screen)) {
      return;
    }

    this.hoverPointerScreen = screen;

    if (this.pointerOverChrome(pointer)) {
      return;
    }

    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const pose = this.pose;
    const projected = this.projectionFor(pose.speed);
    const legal = isValidDestination(
      pose,
      world,
      projectMovementArea(pose, this.reachRadiusPx(), { x: 0, y: 0 }, projected.coastPx),
      projected.axes,
    );
    const next = legal ? { x: world.x, y: world.y } : null;
    const same =
      (next === null && this.hover === null) ||
      (next !== null &&
        this.hover !== null &&
        next.x === this.hover.x &&
        next.y === this.hover.y);

    if (same) {
      return;
    }

    this.hover = next;
  }

  private pointerOverChrome(pointer: Phaser.Input.Pointer): boolean {
    const rect = this.game.canvas.getBoundingClientRect();
    const clientX = rect.left + pointer.x;
    const clientY = rect.top + pointer.y;

    return document.elementFromPoint(clientX, clientY)?.closest('.ui-chrome') != null;
  }

  private paintGhostOval(
    gfx: Phaser.GameObjects.Graphics,
    center: Vec2,
    heading: number,
    axes: ReachAxes,
    color: number,
    zoom: number,
  ): void {
    const points = reachWorldPoints(center, heading, axes);
    fillReach(gfx, points, color, WIND_GHOST_FILL_ALPHA);
    gfx.lineStyle(AREA_STROKE_PX / zoom, color, WIND_GHOST_STROKE_ALPHA);
    strokeChain(gfx, points);
  }

  private paintCompareGhosts(
    gfx: Phaser.GameObjects.Graphics,
    pose: BoatState,
    zoom: number,
  ): void {
    if (this.compareIds.size === 0) {
      return;
    }

    const hover = this.hover;
    const landed = hover ? this.landedMove(pose, hover) : null;

    for (const id of this.compareIds) {
      const entry = this.catalog.find((candidate) => candidate.id === id);

      if (!entry) {
        continue;
      }

      const color = rgb24(entry.boat.hull);
      const live = this.projectionFor(pose.speed, entry.boat);
      this.paintGhostOval(
        gfx,
        this.coastCenter(pose, live.coastPx),
        pose.heading,
        live.axes,
        color,
        zoom,
      );

      if (landed) {
        const next = this.projectionFor(landed.speed, entry.boat);
        this.paintGhostOval(
          gfx,
          this.coastCenter(landed, next.coastPx),
          landed.heading,
          next.axes,
          color,
          zoom,
        );
      }
    }
  }

  private drawChrome(): void {
    const gfx = this.chrome;
    const hull = this.hull;

    if (!gfx || !hull) {
      return;
    }

    const zoom = this.cameras.main.zoom;
    const stroke = 1.5 / zoom;
    const aim = DEFAULT_AIM_CHROME;
    const pose = this.pose;

    gfx.clear();

    if (!this.resolving) {
      this.paintCompareGhosts(gfx, pose, zoom);
      const live = this.projectionFor(pose.speed);
      gfx.lineStyle(stroke, rgb24(aim.movement), 0.95);
      strokeChain(
        gfx,
        reachWorldPoints(this.coastCenter(pose, live.coastPx), pose.heading, live.axes),
      );

      const hover = this.hover;

      if (hover) {
        const path = projectMovePath(pose, hover, 'arc', 0);
        const line = path ? clipMovePathAheadOfBow(path, pose) : [];

        if (line.length >= 2) {
          gfx.lineStyle(2 / zoom, MOVE_LINE, aim.moveLine.a);
          strokeChain(gfx, line);
        }

        const landed = this.landedMove(pose, hover);
        const next = this.projectionFor(landed.speed);
        gfx.lineStyle(stroke, rgb24(aim.prediction), 0.95);
        strokeChain(
          gfx,
          reachWorldPoints(this.coastCenter(landed, next.coastPx), landed.heading, next.axes),
        );
        gfx.fillStyle(rgb24(aim.dest), aim.dest.a);
        gfx.fillCircle(hover.x, hover.y, 4 / zoom);
      }
    }
  }

  private syncDom(): void {
    const parent = this.game.canvas.parentElement;
    const id = this.fileName ? this.fileName.replace(/\.json$/i, '') : 'untitled';
    parent?.setAttribute('data-ready', 'true');
    parent?.setAttribute('data-boat-workshop', id);
  }

  private async paintHull(): Promise<void> {
    const source = this.svgSource ?? (await loadBoatSvgSource());
    this.svgSource = source;
    const hull = this.hull;

    if (!source || !hull?.scene || !this.sys.isActive()) {
      return;
    }

    const token = ++this.liveryToken;
    const key = `boat-livery-${token}`;
    const painted = await loadPaintedBoatTexture(this, source, this.boat, key);

    if (!painted || !this.sys.isActive() || token !== this.liveryToken || !this.hull?.scene) {
      if (painted && this.textures.exists(painted)) {
        this.textures.remove(painted);
      }

      return;
    }

    this.hull.setTexture(painted);
    this.placeHull();

    if (this.liveryKey && this.liveryKey !== painted) {
      this.textures.remove(this.liveryKey);
    }

    this.liveryKey = painted;
  }

  private async refreshTestMaps(): Promise<void> {
    const catalog = await loadMapCatalog();

    if (!this.sys.isActive()) {
      return;
    }

    this.testMaps = mapTestChoices(catalog);
    this.workshop?.sync();
  }

  private async handleTestMap(id: string): Promise<void> {
    const catalog = await loadMapCatalog();

    if (!this.sys.isActive()) {
      return;
    }

    const entry = findMapCatalogEntry(id, catalog);

    if (!entry || !validateMap(entry.map).playable) {
      this.workshop?.showMessage('That map cannot be raced.', 'error');
      await this.refreshTestMaps();
      return;
    }

    saveBoatDraft(this.boat, this.fileName);
    this.scene.start(
      'GameScene',
      handoffFromWorkshop(this.boat, entry, this.fileName, this.savedJson),
    );
  }

  private canvasPointFromClient(clientX: number, clientY: number): Vec2 {
    const rect = this.game.canvas.getBoundingClientRect();
    const width = rect.width || 1;
    const height = rect.height || 1;

    return {
      x: ((clientX - rect.left) / width) * this.scale.width,
      y: ((clientY - rect.top) / height) * this.scale.height,
    };
  }

  private readonly onNativePointerDown = (event: PointerEvent): void => {
    if (!this.sys.isActive() || event.button !== 2 || this.panPointerId !== null) {
      return;
    }

    if (event.target instanceof Element && event.target.closest('.ui-chrome')) {
      return;
    }

    event.preventDefault();
    const point = this.canvasPointFromClient(event.clientX, event.clientY);
    this.panPointerId = event.pointerId;
    this.rightOrigin = point;
    this.cameraControl.armPan(this.cameras.main, point.x, point.y);

    try {
      this.game.canvas.setPointerCapture(event.pointerId);
    } catch {
      // A synthetic press has no capture target; move and up still reach the window.
    }
  };

  private readonly onNativePointerMove = (event: PointerEvent): void => {
    if (this.panPointerId === null || event.pointerId !== this.panPointerId || !this.rightOrigin) {
      return;
    }

    event.preventDefault();
    const point = this.canvasPointFromClient(event.clientX, event.clientY);
    const dx = point.x - this.rightOrigin.x;
    const dy = point.y - this.rightOrigin.y;
    const cam = this.cameras.main;

    if (
      !this.cameraControl.panning &&
      dx * dx + dy * dy >= PAN_DRAG_THRESHOLD_PX * PAN_DRAG_THRESHOLD_PX
    ) {
      cam.stopFollow();
      this.tracking = false;
      this.cameraControl.panning = true;
      this.hover = null;
    }

    if (this.cameraControl.panning) {
      this.cameraControl.applyPan(cam, point.x, point.y);
    }
  };

  private readonly onNativePointerUp = (event: PointerEvent): void => {
    if (this.panPointerId === null || event.pointerId !== this.panPointerId) {
      return;
    }

    event.preventDefault();
    const wasPanning = this.cameraControl.panning;
    this.panPointerId = null;
    this.rightOrigin = null;

    try {
      this.game.canvas.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may already have been released.
    }

    this.cameraControl.endPan();

    if (wasPanning) {
      this.resumeFollow();
    }
  };

  private readonly onPointerDown = (pointer: Phaser.Input.Pointer): void => {
    if (this.pointerOverChrome(pointer) || !pointer.leftButtonDown()) {
      return;
    }

    this.leftOrigin = { x: pointer.x, y: pointer.y };
  };

  private readonly onPointerUp = (pointer: Phaser.Input.Pointer): void => {
    if (pointer.button !== 0) {
      return;
    }

    const origin = this.leftOrigin;
    this.leftOrigin = null;

    if (!origin || this.resolving || this.pointerOverChrome(pointer)) {
      return;
    }

    const dx = pointer.x - origin.x;
    const dy = pointer.y - origin.y;

    if (dx * dx + dy * dy > PAN_DRAG_THRESHOLD_PX * PAN_DRAG_THRESHOLD_PX) {
      return;
    }

    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const destination = this.hover ?? { x: world.x, y: world.y };
    this.commitDestination(destination);
  };

  private readonly onWheel = (
    pointer: Phaser.Input.Pointer,
    _over: Phaser.GameObjects.GameObject[],
    _deltaX: number,
    deltaY: number,
  ): void => {
    const cam = this.cameras.main;
    const auto = this.playCameraTarget().autoZoom;
    this.userZoomFactor = nextUserZoomFactor(this.userZoomFactor, auto, deltaY > 0);
    const nextZoom = appliedZoom(auto, this.userZoomFactor);

    if (this.tracking && !this.cameraControl.panning) {
      if (nextZoom === cam.zoom) {
        return;
      }

      cam.setZoom(nextZoom);
      this.cameraControl.constrain(cam);
      return;
    }

    this.cameraControl.applyZoom(cam, nextZoom, pointer.x, pointer.y);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (this.booting || overlayCapturesKeys()) {
      return;
    }

    if (
      event.code === 'KeyP' &&
      !event.repeat &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey &&
      !event.shiftKey
    ) {
      event.preventDefault();
      this.setRingMode(nextRingMode(this.ringMode));
      return;
    }

    const ctrlOrCmd = event.ctrlKey || event.metaKey;

    if (!ctrlOrCmd) {
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'z' && event.shiftKey) {
      event.preventDefault();
      this.handleRedo();
      return;
    }

    if (key === 'z') {
      event.preventDefault();
      this.handleUndo();
      return;
    }

    if (key === 'y') {
      event.preventDefault();
      this.handleRedo();
      return;
    }

    if (key === 's') {
      event.preventDefault();
      void this.handleSave();
    }
  };
}
